export const DEFAULT_MAX_BYTES = 20 * 1024 * 1024; // 20 MB

export type SanitizeOptions = {
  maxBytes?: number;
  acceptMime?: string[];   // allowed MIME types
  acceptExt?: string[];    // allowed extensions (lowercase, without dot)
};

export type SanitizeResult = {
  ok: true;
  file: File;
  size: number;
  maxBytes: number;
} | {
  ok: false;
  file: File;
  size: number;
  maxBytes: number;
  reason: 'too_large' | 'type_not_allowed';
  message: string;
};

const DEFAULT_MIME: string[] = [
  'audio/mpeg', 'audio/mp3',
  'audio/wav', 'audio/x-wav',
  'audio/aac', 'audio/x-aac',
  'audio/flac', 'audio/x-flac',
  'audio/ogg', 'audio/webm'
];

const DEFAULT_EXT: string[] = [
  'mp3', 'wav', 'aac', 'm4a', 'flac', 'ogg', 'oga', 'opus', 'webm'
];

export function formatBytes(n: number): string {
  if (!Number.isFinite(n)) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : v < 10 ? 2 : 1)} ${u[i]}`;
}

function extOf(name: string): string {
  const base = name?.split('?')[0] ?? '';
  const parts = base.split('.');
  if (parts.length <= 1) return '';
  return parts.pop()!.toLowerCase();
}

export function sanitizeFile(file: File, opts: SanitizeOptions = {}): SanitizeResult {
  const maxBytes = opts.maxBytes ?? DEFAULT_MAX_BYTES;
  const acceptMime = (opts.acceptMime && opts.acceptMime.length ? opts.acceptMime : DEFAULT_MIME).map(s => s.toLowerCase());
  const acceptExt = (opts.acceptExt && opts.acceptExt.length ? opts.acceptExt : DEFAULT_EXT).map(s => s.toLowerCase());

  const size = file.size ?? 0;
  if (size > maxBytes) {
    return {
      ok: false,
      file,
      size,
      maxBytes,
      reason: 'too_large',
      message: `File is too large (${formatBytes(size)}). Max allowed is ${formatBytes(maxBytes)}.`
    };
  }

  const mime = String(file.type || '').toLowerCase();
  const ext = extOf(file.name || '');

  const mimeOk = mime ? acceptMime.includes(mime) || acceptMime.some(m => m.endsWith('/*') && mime.startsWith(m.slice(0, -1))) : false;
  const extOk = ext ? acceptExt.includes(ext) : false;

  if (!mimeOk && !extOk) {
    return {
      ok: false,
      file,
      size,
      maxBytes,
      reason: 'type_not_allowed',
      message: `Unsupported file type${ext ? ` (.${ext})` : ''}. Allowed: ${acceptExt.map(e => `.${e}`).join(', ')}.`
    };
  }

  return { ok: true, file, size, maxBytes };
}

export function sanitizeFiles(files: Array<File> | FileList, opts?: SanitizeOptions) {
  const list = Array.from(files as Array<File>);
  const results = list.map(f => sanitizeFile(f, opts));
  const accepted = results.filter(r => r.ok).map(r => (r as Extract<SanitizeResult, { ok: true }>).file);
  const rejected = results.filter(r => !r.ok) as Extract<SanitizeResult, { ok: false }>[];
  return { results, accepted, rejected };
}
